import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, Coins } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { openBillingPortal, getCreditBalance, BillingError, CREDIT_PACKS, type CreditPack } from "@/lib/billing";
import { CreditPackButton } from "@/components/CreditPackButton";
import { trackEvent, trackConversion } from "@/lib/gtag";
import { formatCurrency } from "@/lib/utils";

// Per-session-id dedupe key — Stripe's success URL contains session_id, so we only fire
// the conversion once even if the user reloads /billing?status=success multiple times.
const SESSION_KEY_PREFIX_PURCHASE_TRACKED = "doclens.session.purchaseTracked.";

interface Account {
  plan: string;
  subscription_status: string | null;
  current_period_end: string | null;
  stripe_customer_id: string | null;
}

export function BillingPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const status = params.get("status");

  const [account, setAccount] = useState<Account | null>(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  useEffect(() => {
    if (status === "success") {
      toast.success("Subscription activated. Welcome to the paid tier.");
    } else if (status === "credits_success") {
      toast.success("Credits added to your account.");
    }

    // Fire Google Ads conversion exactly once per checkout session.
    // session_id comes from Stripe's {CHECKOUT_SESSION_ID} placeholder in our success_url.
    const sessionId = params.get("session_id");
    if (sessionId && (status === "success" || status === "credits_success")) {
      const dedupeKey = SESSION_KEY_PREFIX_PURCHASE_TRACKED + sessionId;
      if (!sessionStorage.getItem(dedupeKey)) {
        sessionStorage.setItem(dedupeKey, "1");
        if (status === "success") {
          trackEvent("purchase_subscription");
          trackConversion(import.meta.env.VITE_GTAG_CONV_SUBSCRIPTION);
        } else {
          trackEvent("purchase_credits");
          trackConversion(import.meta.env.VITE_GTAG_CONV_CREDIT_PURCHASE);
        }
      }
    }
  }, [status, params]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function load() {
      const [{ data }, balance] = await Promise.all([
        supabase
          .from("accounts")
          .select("plan, subscription_status, current_period_end, stripe_customer_id")
          .eq("user_id", user!.id)
          .maybeSingle(),
        getCreditBalance(),
      ]);
      if (!cancelled) {
        setAccount(data as Account | null);
        setCreditBalance(balance);
        setLoadingAccount(false);
      }
    }

    load();

    // Re-poll a few times if we just returned from Checkout — the webhook may still be
    // in flight, and we don't want to show the stale "free" plan to a user who just paid.
    let polls = 0;
    let interval: ReturnType<typeof setInterval> | undefined;
    if (status === "success" || status === "credits_success") {
      interval = setInterval(() => {
        polls++;
        load();
        if (polls > 6 && interval !== undefined) clearInterval(interval);
      }, 1000);
    }

    return () => {
      cancelled = true;
      if (interval !== undefined) clearInterval(interval);
    };
  }, [user, status]);

  async function handlePortal() {
    setPortalLoading(true);
    try {
      await openBillingPortal();
    } catch (err) {
      toast.error(err instanceof BillingError ? err.message : "Could not open billing portal");
      setPortalLoading(false);
    }
  }

  if (loadingAccount) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-neutral-500 text-sm">
        Loading account…
      </div>
    );
  }

  const plan = account?.plan ?? "free";
  const isPaid = plan !== "free";
  const periodEnd = account?.current_period_end ? new Date(account.current_period_end) : null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 md:py-16 space-y-8">
      {status === "success" && (
        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-6 flex items-start gap-3 animate-fade-in">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h2 className="font-semibold text-emerald-900">Subscription activated</h2>
            <p className="text-sm text-emerald-800">
              Your plan is updating in the background — refresh this page in a moment if it
              still shows the old tier.
            </p>
          </div>
        </div>
      )}

      <header className="space-y-2">
        <h1 className="text-3xl md:text-4xl font-bold">Billing</h1>
        <p className="text-neutral-600">Manage your DocLens subscription.</p>
      </header>

      {status === "credits_success" && (
        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-6 flex items-start gap-3 animate-fade-in">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h2 className="font-semibold text-emerald-900">Credits added</h2>
            <p className="text-sm text-emerald-800">
              The pack you bought is now in your account — keep uploading.
            </p>
          </div>
        </div>
      )}

      <section className="rounded-xl border border-neutral-200 bg-white p-6 space-y-4">
        <h2 className="font-semibold">Current plan</h2>
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-3xl font-bold capitalize">{plan}</div>
            {account?.subscription_status && (
              <div className="text-sm text-neutral-500 capitalize">
                {account.subscription_status.replaceAll("_", " ")}
              </div>
            )}
            {periodEnd && isPaid && (
              <div className="text-xs text-neutral-500 mt-1">
                Renews {periodEnd.toLocaleDateString()}
              </div>
            )}
          </div>
          <Link
            to="/pricing"
            className="text-sm text-brand hover:underline"
          >
            {isPaid ? "Change plan →" : "See plans →"}
          </Link>
        </div>
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-600" />
            Credits
          </h2>
          <div className="text-2xl font-bold tabular-nums">{creditBalance ?? "—"}</div>
        </div>
        <p className="text-sm text-neutral-600">
          Credits are used when you go over your plan quota. They never expire and stack with
          your subscription.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(Object.keys(CREDIT_PACKS) as CreditPack[]).map((id) => {
            const pack = CREDIT_PACKS[id];
            return (
              <CreditPackButton
                key={id}
                pack={id}
                variant={id === "standard" ? "primary" : "secondary"}
              >
                {pack.credits} credits · {formatCurrency(pack.priceUsd)}
              </CreditPackButton>
            );
          })}
        </div>
      </section>

      {isPaid ? (
        <section className="rounded-xl border border-neutral-200 bg-white p-6 space-y-3">
          <h2 className="font-semibold">Manage subscription</h2>
          <p className="text-sm text-neutral-600">
            Update your card, view invoices, change plan, or cancel via Stripe's billing portal.
          </p>
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-neutral-800 transition disabled:opacity-60"
          >
            {portalLoading ? "Opening…" : "Open billing portal"}
            <ExternalLink className="h-4 w-4" />
          </button>
        </section>
      ) : (
        <section className="rounded-xl border-2 border-brand bg-brand-50 p-6 space-y-3">
          <h2 className="font-semibold text-brand-900">Upgrade for more documents</h2>
          <p className="text-sm text-brand-900/80">
            Hitting your 1-per-day limit? Starter ($9/mo) gets you 50, Pro ($29/mo) unlocks
            200 + API access.
          </p>
          <Link
            to="/pricing"
            className="inline-block rounded-lg bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-700 transition"
          >
            See plans
          </Link>
        </section>
      )}

      <section className="text-xs text-neutral-500 space-y-1">
        <p>
          Billing is handled by{" "}
          <a href="https://stripe.com" className="underline" target="_blank" rel="noreferrer">
            Stripe
          </a>
          . DocLens never sees or stores your card details.
        </p>
        <p>
          Questions? Email{" "}
          <a href="mailto:billing@doclens.app" className="underline">
            billing@doclens.app
          </a>
          .
        </p>
      </section>
    </div>
  );
}
